// Central source of JSON-LD structured data for rich Google results.
// Injected at runtime via useSEO to bypass CRA HTML minifier stripping.

// Use the actual domain the site is being served from at runtime.
// This avoids hardcoding a specific production URL that could drift out of sync.
export const SITE_URL =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "";
export const BUSINESS_ID = `${SITE_URL}/#business`;

export const LOCAL_BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "PestControl",
  "@id": BUSINESS_ID,
  name: "Blue Collar World Pest Control",
  legalName: "Blue Collar World LLC",
  image: "https://images.pexels.com/photos/195220/pexels-photo-195220.jpeg",
  logo: "https://customer-assets.emergentagent.com/job_site-builder-3572/artifacts/int9cqaw_FE1D1B5F-B5C0-402C-A181-71BFC9E298FA.png",
  telephone: "+1-484-764-3392",
  email: "raymondtjackson@bluecollarworldpestcontrolservices.com",
  url: `${SITE_URL}/`,
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Credit Card, Check",
  address: {
    "@type": "PostalAddress",
    streetAddress: "904 Bernd Street",
    addressLocality: "Pittsburgh",
    addressRegion: "PA",
    postalCode: "15210",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 40.4106, longitude: -79.9959 },
  areaServed: [
    { "@type": "City", name: "Pittsburgh" },
    { "@type": "AdministrativeArea", name: "Allegheny County" },
    { "@type": "Place", name: "Mt. Lebanon" },
    { "@type": "Place", name: "Squirrel Hill" },
    { "@type": "Place", name: "South Side" },
    { "@type": "Place", name: "Brookline" },
    { "@type": "Place", name: "Beechview" },
    { "@type": "Place", name: "Bethel Park" },
    { "@type": "Place", name: "Dormont" },
    { "@type": "Place", name: "Carrick" },
  ],
  identifier: "PA License #BU15680",
  knowsLanguage: ["en", "es"],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "07:00",
      closes: "20:00",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Pest Control Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Household Pest Control" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Parks & Schools Pest Management" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Termite & Wood-Destroying Insect Treatment" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Wildlife Removal" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mosquito Control" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Bed Bug Extermination" } },
    ],
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "200",
    bestRating: "5",
    worstRating: "1",
  },
};

export const HOME_FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Are you licensed to do pest control in Pittsburgh, PA?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Blue Collar World Pest Control operates under Pennsylvania Pesticide Business License #BU15680 and employs state-certified applicators. We are fully insured for households, parks, and schools throughout Allegheny County.",
      },
    },
    {
      "@type": "Question",
      name: "Do you offer Spanish-speaking service?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sí. Se habla español. Our team includes bilingual technicians and support staff to serve Pittsburgh's Spanish-speaking community.",
      },
    },
    {
      "@type": "Question",
      name: "How much does pest control cost in Pittsburgh?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We offer two plans: a Quarterly service plan at standard rate that includes 4 scheduled visits per year with free re-treatment, and a One-Time premium treatment for severe infestations. Call 484-764-3392 for a property-specific quote.",
      },
    },
    {
      "@type": "Question",
      name: "What pests do you treat?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We specialize in termites and wood-destroying insects, wildlife removal (raccoons, groundhogs, bats, squirrels), bed bugs, mosquitoes, and general household pests including roaches, ants, rodents, and spiders.",
      },
    },
    {
      "@type": "Question",
      name: "Are your treatments safe for kids and pets?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. We use EPA-compliant methods and offer family & pet safe options. Our school and playground programs follow strict child-safe protocols with full documentation.",
      },
    },
    {
      "@type": "Question",
      name: "How fast can you come out?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Same-week scheduling is available for one-time treatments. We also operate a 24/7 emergency line at 484-764-3392 for urgent issues.",
      },
    },
  ],
};

// Builds a Service-specific JSON-LD block for /services/:slug pages
export const buildServiceJsonLd = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: service.title,
  description: service.hero,
  provider: { "@id": BUSINESS_ID },
  areaServed: [
    { "@type": "City", name: "Pittsburgh" },
    { "@type": "AdministrativeArea", name: "Allegheny County" },
  ],
  serviceType: service.title,
  url: `${SITE_URL}/services/${service.slug}`,
});

// Builds a FAQ-specific block from a service's FAQs
export const buildServiceFaqJsonLd = (service) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: service.faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// Builds an Article/BlogPosting JSON-LD block for /blog/:slug pages
export const buildBlogPostJsonLd = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.metaDescription || post.excerpt,
  author: { "@type": "Organization", name: post.author || "Blue Collar World Pest Control" },
  publisher: { "@id": BUSINESS_ID },
  datePublished: post.date,
  dateModified: post.date,
  url: `${SITE_URL}/blog/${post.slug}`,
  articleSection: post.category,
  keywords: post.category,
});
